import _ from 'lodash';
import moment from 'moment';

// Firebase App (the core Firebase SDK) is always required and must be listed first
import { getApps, getApp, initializeApp } from 'firebase/app';
import { getFirestore, initializeFirestore, connectFirestoreEmulator, doc, collection, onSnapshot, getDocs } from 'firebase/firestore';

// import WebStorageManager from '../WebStorageManager';

import DefaultConfig from './DefaultConfig';
import onSnapshotWrapper from './onSnapshotWrapper';
import Config from "../Config";

const WebStorageManager = require('../WebStorageManager');

let __subscription_timestamp__ = null;

class FirebaseWrapper {
    constructor(config = DefaultConfig.default_config, collection_name, subcollection_name, snapshot_params, prefix_message, _onSnapshotWrapper = onSnapshotWrapper) {
        this._config = config;

        const force_long_polling = WebStorageManager.getFromWebStorage('force_long_polling');
        // Safari blocks Firestore’s default Fetch-based streaming transport with CORS errors.
        // Detect Safari (not Chrome/Android which also include "Safari" in their UA) and force long-polling.
        const is_safari = typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        const initialize_mode = (getApps().length === 0);

        if (initialize_mode) {
            this._app = initializeApp(config);
        } else {
            this._app = getApp(); // if already initialized, use that one
        }

        if (initialize_mode) {
            // experimentalForceLongPolling / experimentalAutoDetectLongPolling are Firestore settings,
            // not Firebase app settings — they must be passed to initializeFirestore(), not initializeApp().
            //
            // experimentalForceLongPolling: forces XHR long-polling. Each response from the backend is
            // closed immediately after data is sent (unlike streaming, which keeps the connection open).
            // Used for Safari (AR-3521): Safari's strict CORS policy blocks Firestore's default
            // Fetch-based streaming transport. Minor performance cost vs streaming.
            // Cannot be combined with experimentalAutoDetectLongPolling.
            //
            // experimentalAutoDetectLongPolling: the SDK automatically detects incompatible environments
            // (certain proxies, antivirus software, etc.) and falls back to long-polling when needed.
            // Used for all non-Safari browsers as a safe default.
            const firestoreSettings = (force_long_polling || is_safari)
                ? { experimentalForceLongPolling: true }
                : { experimentalAutoDetectLongPolling: true };
            this._firestore = initializeFirestore(this._app, firestoreSettings);
        } else {
            this._firestore = getFirestore(this._app);
        }

        if (initialize_mode && config.useEmulator) {
            connectFirestoreEmulator(this._firestore, config.emulatorHost, config.emulatorPort);
        }

        this.collection_name = collection_name;
        this.subcollection_name = subcollection_name;
        this.snapshot_params = snapshot_params;
        this.prefix_message = prefix_message;

        this._onSnapshotWrapper = _onSnapshotWrapper;
        this._subscriptions = {};
    }

    // register_snapshot_subcollection_callback(main_collection_id, subcollection_name, onSnapshot, onSnapshotError) {
    //     // See: https://cloud.google.com/firestore/docs/query-data/queries
    //     // See: https://cloud.google.com/firestore/docs/query-data/listen
    //     return this._firestore
    //         .collection(this.collection_name)
    //         .doc(main_collection_id)
    //         .collection(subcollection_name)
    //         .onSnapshot(onSnapshot, onSnapshotError);
    //     //.where(query_field_path, query_operator, query_value).onSnapshot(onSnapshot);
    // }

    _setExpiration(token, onTimeout) {
        const milliseconds = moment.duration(this._config.subscriptionTimeoutInSeconds, 'seconds').asMilliseconds();
        setTimeout(() => {
            this.unsubscribe(token);
            if (_.isFunction(onTimeout)) {
                onTimeout();
            }
        }, milliseconds);
    }

    subscribe(token, onData, onError, onTimeout, pollingTimeout) {
        if (token && _.isFunction(onData)) {
            this.unsubscribe(token);

            __subscription_timestamp__ = moment();
            const _onData = this._onSnapshotWrapper(onData, this.snapshot_params, this.prefix_message, __subscription_timestamp__);
            const _onError = _.isFunction(onError)
                ? onError
                : _.noop;

            const tokenDocumentRef = doc(this._firestore, this.collection_name, token);
            const subCollectionRef = collection(tokenDocumentRef, this.subcollection_name);
            if (Config.localhost && pollingTimeout) {
                this._subscriptions[token] = true;
                this._fetchLatestData(this, subCollectionRef, token, onData, pollingTimeout);

                // lets listen localhost for 40 sec
                const milliseconds = 28000;
                const self = this;
                setTimeout(() => {
                    self._subscriptions[token] = null;
                    if (_.isFunction(onTimeout)) {
                        onTimeout();
                    }
                }, milliseconds);
            } else {
                try {
                    this._subscriptions[token] = onSnapshot(subCollectionRef, _onData, _onError);
                } catch (error) {
                    console.error('ListenerSubscriber: onSnapshot failed', error);
                    _onError(error);
                }
            }

            this._setExpiration(token, onTimeout);
        }
    }

    async _fetchLatestData(self, subCollectionRef, token, onData, pollingTimeout) {
        const querySnapshot = await getDocs(subCollectionRef);
        const deals = [];
        const message_type = []
        querySnapshot.forEach(doc => {
            const dealsFromString = _.isString(doc.data().deals) ? JSON.parse(doc.data().deals) : doc.data().deals;
            if (_.isArray(dealsFromString)) {
                deals.push(...(dealsFromString))
            }
            message_type.push(doc.data().message_type);
        });


        onData({ message_type, deals});
        setTimeout(() => {
            if (self._subscriptions[token]) {
                self._fetchLatestData(self, subCollectionRef, token, onData)
            }
        }, pollingTimeout);
    }

    unsubscribe(token) {
        const unsub = this._subscriptions[token];
        if (_.isFunction(unsub)) {
            this._subscriptions[token] = null;
            unsub();
        }
    }

    unsubscribeAll() {
        for (const token in this._subscriptions) {
            this.unsubscribe(token);
        }
    }
}

// Move to: If you use reserved Hosting URLs,  or to env variables
// your Firebase config is automatically pulled from your Firebase project, 
// so you don't need to explicitly provide the object in your code.

// const firebase_wrapper = (collection_name = default_collection, subcollection_name = default_subcollection, field_name = default_field) => new FirebaseWrapper(default_config, collection_name, subcollection_name, field_name);
export default FirebaseWrapper;